import {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useClassificationConfig,
  useItems,
  useLanguages,
  useLocations,
  useSetItemFlags,
  useTopics
} from '../api/hooks';
import {getApiErrorMessage} from '../api/client';
import {type FlagColor} from '../api/flags';
import {
  DEFAULT_ITEM_SORT,
  ITEM_SORTS,
  type Item,
  type ItemSort,
  type ItemsQuery
} from '../api/items';
import {ClassifyDialog} from '../components/investigations/ClassifyDialog';
import {ItemDetailsSheet} from '../components/items/ItemDetailPanel';
import {
  CHANNEL_LIMIT,
  ContentChannels,
  useContentChannels
} from '../components/items/ContentChannels';
import {FiltersMenu, itemFilterQuery} from '../components/items/ItemFiltersMenu';
import {ItemsCollection} from '../components/items/ItemsCollection';
import {GRID_COLS_BY_SIZE} from '../components/items/gridColumns';
import {ItemsBulkBar, ItemsToolbar} from '../components/items/ItemsToolbar';
import {SavedViewsMenu} from '../components/items/SavedViewsMenu';
import {useSetItemFlag} from '../components/items/useSetItemFlag';
import {CreateP0AlertDialog, useP0AlertDialog} from '../components/p0alerts/CreateP0AlertDialog';
import {SensitiveMediaToggle} from '../components/shared/SensitiveMedia';
import {isFlagFilter, matchesFlagFilter, type FlagFilter} from '../lib/flagFilter';
import {contentViewFilters, toContentViewQuery, type ContentViewQuery} from '../lib/savedViews';
import {DEFAULT_ITEM_FILTERS, type ItemFilterState, useUiStore} from '../store/uiStore';
import {itemIdsForClipboard} from '../lib/itemIds';
import {useDebounce} from '../lib/useDebounce';
import {showToast} from '../lib/toast';
import {cn} from '@/lib/utils';

const PAGE_SIZE = 100;

// The Filters menu's state is the same shape Investigations keeps per investigation; Content is one
// more key in the same store, so a filtered view is remembered across reloads.
const FILTERS_KEY = 'content';

const isItemSort = (value: string | null): value is ItemSort =>
  value !== null && (ITEM_SORTS as readonly string[]).includes(value);

// Every item PRI holds, across all investigations - a direct peer of Submissions. Search, sort, flag
// filter, tab and page live in the URL, so a view survives a reload and can be linked to; the
// Filters menu's attributes live in the UI store. Search is server-side (q), so it covers every item
// rather than just the page on screen. The toolbar follows the investigations one: Items | Channels,
// search, flags, Filters, then sort and layout on the right.
//
// The caller's flagged items are this page with `?flag=any&sort=flagged_desc` (the built-in "My
// flagged items" view). The "Date flagged" sort orders by the caller's own flag time, so it is
// offered only while a flag filter narrows the page to items that have one.
export function ItemsPage() {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const q = searchParameters.get('q') ?? '';
  const platform = searchParameters.get('platform');
  const affiliate = searchParameters.get('affiliate');
  const flags = searchParameters.getAll('flag').filter(isFlagFilter);
  const rawSort = searchParameters.get('sort');
  // A linked or remembered flagged_desc without a flag filter falls back rather than sorting every
  // item by a flag time almost none of them have.
  const sort =
    isItemSort(rawSort) && (rawSort !== 'flagged_desc' || flags.length > 0)
      ? rawSort
      : DEFAULT_ITEM_SORT;
  const page = Math.max(Number(searchParameters.get('page')) || 1, 1);
  const tab = searchParameters.get('tab') === 'channels' ? 'channels' : 'items';

  const filters = useUiStore((s) => s.itemFiltersByKey[FILTERS_KEY] ?? DEFAULT_ITEM_FILTERS);
  const setFiltersByKey = useUiStore((s) => s.setItemFilters);
  const thumbnailSize = useUiStore((s) => s.thumbnailSize);
  const setFilters = useCallback(
    (patch: Partial<ItemFilterState>) => {
      setFiltersByKey(FILTERS_KEY, patch);
    },
    [setFiltersByKey]
  );
  // The number boxes are debounced so each keystroke doesn't fire a request over every item.
  const minSubscribers = useDebounce(filters.minSubscribersFilter, 400);
  const minViews = useDebounce(filters.minViewsFilter, 400);
  const maxSubscribers = useDebounce(filters.maxSubscribersFilter, 400);
  const maxViews = useDebounce(filters.maxViewsFilter, 400);
  const minPins = useDebounce(filters.minPinsFilter, 400);
  const minBoards = useDebounce(filters.minBoardsFilter, 400);
  const minSaves = useDebounce(filters.minSavesFilter, 400);
  const minDuration = useDebounce(filters.minDurationFilter, 400);
  const maxDuration = useDebounce(filters.maxDurationFilter, 400);
  const filterParameters = itemFilterQuery({
    ...filters,
    // Hidden from the menu on this page (it has no scope for "new" to be relative to), so a value
    // persisted from before can't keep narrowing the results unseen.
    newOnlyFilter: false,
    minSubscribersFilter: minSubscribers,
    minViewsFilter: minViews,
    maxSubscribersFilter: maxSubscribers,
    maxViewsFilter: maxViews,
    minPinsFilter: minPins,
    minBoardsFilter: minBoards,
    minSavesFilter: minSaves,
    minDurationFilter: minDuration,
    maxDurationFilter: maxDuration
  });

  const [search, setSearch] = useState(q);
  const debouncedSearch = useDebounce(search, 300);

  // Follow a q changed from outside (the nav link, browser back). Our own write always lands as
  // q === debouncedSearch, so skipping that case never clobbers what the analyst is still typing.
  useEffect(() => {
    if (q !== debouncedSearch) {
      setSearch(q);
    }
    // Only an external q change should resync; debouncedSearch is read, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Writes a change into the URL, dropping back to page 1 for anything but paging itself.
  const update = (change: (next: URLSearchParams) => void, keepPage = false) => {
    const next = new URLSearchParams(searchParameters);
    change(next);
    if (!keepPage) {
      next.delete('page');
    }

    setSearchParameters(next, {replace: true});
  };

  useEffect(() => {
    if (debouncedSearch === q) {
      return;
    }

    update((next) => {
      if (debouncedSearch) {
        next.set('q', debouncedSearch);
      } else {
        next.delete('q');
      }
    });
    // Only a settled search should write the URL; `update` closes over the current params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // What both tabs narrow by. Built without explicit `undefined` values (exactOptionalPropertyTypes).
  const narrowing: Omit<ItemsQuery, 'page' | 'page_size' | 'sort'> = {...filterParameters};
  if (q) {
    narrowing.q = q;
  }

  if (platform) {
    narrowing.platform = platform;
  }

  if (affiliate) {
    narrowing.affiliate = affiliate;
  }

  if (flags.length > 0) {
    narrowing.flag = flags;
  }

  const parameters: ItemsQuery = {...narrowing, page, page_size: PAGE_SIZE};
  if (sort !== DEFAULT_ITEM_SORT) {
    parameters.sort = sort;
  }

  const setItemFlag = useSetItemFlag();
  const {data, isLoading} = useItems(parameters);
  // Flagging patches the cached page rather than refetching it, so a row that no longer matches the
  // flag filter (unflagged, or recoloured out of it) is dropped here.
  const items = (data?.results ?? []).filter((item) => matchesFlagFilter(item.flag, flags));
  // Fetched only while its tab is open; the Channels tab shows its count once it has one.
  const {data: channelData} = useContentChannels(narrowing, tab === 'channels');
  const channelCount = channelData?.results.length;

  // What a saved view would store for the page as it stands, and putting one in place: the URL's
  // narrowing is replaced (the tab is kept) and the Filters menu is reset to the view's values.
  const currentView = toContentViewQuery({q, flags, sort, platform, affiliate, filters});
  const applyView = (view: ContentViewQuery) => {
    update((params) => {
      for (const key of ['q', 'flag', 'sort', 'platform', 'affiliate']) {
        params.delete(key);
      }

      for (const [key, value] of Object.entries({
        q: view.q,
        sort: view.sort,
        platform: view.platform,
        affiliate: view.affiliate
      })) {
        if (value) {
          params.set(key, value);
        }
      }

      for (const f of view.flag ?? []) {
        params.append('flag', f);
      }
    });
    setFilters(contentViewFilters(view));
  };

  const setFlagFilter = (next: FlagFilter[]) => {
    update((params) => {
      params.delete('flag');
      for (const f of next) {
        params.append('flag', f);
      }

      // "Date flagged" only means something over flagged items, so it goes with the last flag filter.
      if (next.length === 0 && params.get('sort') === 'flagged_desc') {
        params.delete('sort');
      }
    });
  };

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [classifyItems, setClassifyItems] = useState<Item[]>([]);
  // The item whose Show more is open in the details sidebar, and the items it was opened among.
  const [detailsItem, setDetailsItem] = useState<Item | null>(null);
  const [detailsItems, setDetailsItems] = useState<readonly Item[]>([]);
  const showDetails = useCallback((item: Item, siblings: readonly Item[]) => {
    setDetailsItem(item);
    setDetailsItems(siblings);
  }, []);
  const p0Dialog = useP0AlertDialog();
  const {data: classifications = []} = useClassificationConfig();
  const {data: languages = []} = useLanguages();
  const {data: topics = []} = useTopics();
  const {data: locations = []} = useLocations();
  const {mutate: setFlags} = useSetItemFlags();

  // Selection belongs to the page on screen: a different page, filter or sort is a different set.
  const pageKey = `${tab}|${page}|${q}|${sort}|${flags.join(',')}|${platform ?? ''}|${affiliate ?? ''}|${JSON.stringify(filterParameters)}`;
  const [selectionKey, setSelectionKey] = useState(pageKey);
  if (selectionKey !== pageKey) {
    setSelectionKey(pageKey);
    setSelectedIds(new Set());
  }

  const toggleCheck = useCallback((id: string) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const classifyOne = useCallback((item: Item) => {
    setClassifyItems([item]);
  }, []);

  // A channel's items sit in that section's own query, so once a selection spans channels the page
  // can't find a selected item in `items`. The Channels tab hands every item it loads up here.
  const [channelItems, setChannelItems] = useState<ReadonlyMap<string, Item>>(new Map());
  const rememberItems = useCallback((loaded: readonly Item[]) => {
    setChannelItems((previous) => {
      const next = new Map(previous);
      for (const item of loaded) {
        next.set(item.id, item);
      }

      return next;
    });
  }, []);

  const loadedItems = new Map([...channelItems, ...items.map((i): [string, Item] => [i.id, i])]);
  const selectedItems = [...selectedIds].flatMap((id) => loadedItems.get(id) ?? []);
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-4">
        {/* The search, filters and view controls sit on a white band, as the Investigations
            header's do: on the grey content area the controls' own fills blended into it. */}
        <div className="bg-card border-border -mx-4 -mt-4 border-b px-4 py-3">
          <ItemsToolbar
            leading={
              <>
                <Tabs
                  value={tab}
                  className="gap-0"
                  onValueChange={(next) => {
                    update((params) => {
                      if (next === 'channels') {
                        params.set('tab', 'channels');
                      } else {
                        params.delete('tab');
                      }
                    });
                  }}>
                  <TabsList>
                    <TabsTrigger value="items">
                      Items{' '}
                      {data ? (
                        <span className="tabular-nums">{data.total.toLocaleString()}</span>
                      ) : null}
                    </TabsTrigger>
                    <TabsTrigger value="channels">
                      Channels
                      {tab === 'channels' && channelCount !== undefined ? (
                        <span className="tabular-nums">
                          {channelCount.toLocaleString()}
                          {channelCount >= CHANNEL_LIMIT ? '+' : ''}
                        </span>
                      ) : null}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <SavedViewsMenu current={currentView} onApply={applyView} />
              </>
            }
            search={search}
            flags={flags}
            filters={<FiltersMenu filters={filters} showFreshness={false} onChange={setFilters} />}
            sort={sort}
            flaggedSort={flags.length > 0}
            // Blur lives with the view controls, not the page header, as it does on Investigations.
            trailing={<SensitiveMediaToggle />}
            onSearchChange={setSearch}
            onFlagsChange={setFlagFilter}
            onSortChange={(next) => {
              update((params) => {
                if (next === DEFAULT_ITEM_SORT) {
                  params.delete('sort');
                } else {
                  params.set('sort', next);
                }
              });
            }}
          />
        </div>

        {tab === 'channels' ? (
          <ContentChannels
            query={narrowing}
            sort={sort}
            selectedItemIds={selectedIds}
            onToggleCheck={toggleCheck}
            onClassify={classifyOne}
            onSetFlag={setItemFlag}
            onShowDetails={showDetails}
            onItemsLoaded={rememberItems}
          />
        ) : isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Spinner />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>
                {flags.length > 0 ? 'No flagged items match' : 'No items match'}
              </EmptyTitle>
              <EmptyDescription>
                {flags.length > 0
                  ? 'Flag an item from Content, Investigations or Submission and it will be found here, whether or not you submit it.'
                  : 'Try clearing the search or the filters.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ItemsCollection
            items={items}
            gridClassName={cn('grid gap-3', GRID_COLS_BY_SIZE[thumbnailSize])}
            selectedItemIds={selectedIds}
            onToggleCheck={toggleCheck}
            onToggleAll={(select) => {
              setSelectedIds(select ? new Set(items.map((i) => i.id)) : new Set());
            }}
            onClassify={classifyOne}
            onSetFlag={setItemFlag}
            onShowDetails={showDetails}
          />
        )}

        {tab === 'items' && data && (page > 1 || data.has_more) ? (
          // Without an href the links render as buttons, which is right for paging that lives in
          // the query string. The API only says whether there is a next page, not how many, so
          // there are no numbered links.
          <Pagination className="justify-end">
            <PaginationContent>
              <PaginationItem className="text-muted-foreground px-2 text-xs">
                Page {page}
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  // Without an href the link is a native button and forwards `disabled`, but the
                  // package types the props as an anchor's, so it goes past the type as a spread.
                  {...{disabled: page <= 1}}
                  onClick={() => {
                    update((next) => {
                      next.set('page', String(page - 1));
                    }, true);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  {...{disabled: !data.has_more}}
                  onClick={() => {
                    update((next) => {
                      next.set('page', String(page + 1));
                    }, true);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        ) : null}
      </div>

      <div className="sticky bottom-0 -mx-4 -mb-4">
        <ItemsBulkBar
          selectedCount={selectedItems.length}
          totalCount={tab === 'items' ? items.length : selectedItems.length}
          showClearFlag={selectedItems.some((i) => i.flag)}
          {...(tab === 'items'
            ? {
                onSelectAll() {
                  setSelectedIds(new Set(items.map((i) => i.id)));
                }
              }
            : {})}
          copyIds={() => itemIdsForClipboard(selectedItems)}
          onFlag={(color: FlagColor | null) => {
            setFlags(
              {itemIds: selectedItems.map((i) => i.id), color},
              {
                onError(error) {
                  showToast(
                    getApiErrorMessage(
                      error,
                      color ? 'Failed to flag items.' : 'Failed to clear flags.'
                    ),
                    'error'
                  );
                }
              }
            );
          }}
          onClassify={() => {
            setClassifyItems(selectedItems);
          }}
          onClear={clearSelection}
        />
      </div>

      <ItemDetailsSheet
        item={detailsItem}
        items={detailsItems}
        onNavigate={setDetailsItem}
        onClassify={classifyOne}
        onClose={() => {
          setDetailsItem(null);
        }}
      />

      <ClassifyDialog
        items={classifyItems}
        classifications={classifications}
        languages={languages}
        topics={topics}
        locations={locations}
        origin="investigations"
        onClose={() => {
          setClassifyItems([]);
          clearSelection();
        }}
        onSubmitted={(_succeeded, linkedItems, wantP0) => {
          if (wantP0) {
            p0Dialog.launch(linkedItems);
          }
        }}
      />
      <CreateP0AlertDialog {...p0Dialog.dialogProps()} />
    </div>
  );
}
